import { ApplicationRef, ComponentRef, EnvironmentInjector, Injectable, createComponent } from '@angular/core';
import maplibregl from 'maplibre-gl';
import {
  VesselCardComponent, VesselCardParticipantsRequest, VesselCardProfileRequest,
} from '../components/vessel-card/vessel-card.component';
import { CardIdentity, CardParams, CardUnits, ChatArgs } from '../shared/utils/vessel-card.logic';

export interface VesselCardPopupConfig {
  params: CardParams;
  /** A reading the caller already fetched (skips the card's own load). */
  prefetched?: any;
  identity: CardIdentity;
  units: CardUnits;
  chatEnabled: boolean;
  onParticipants: (req: VesselCardParticipantsRequest) => void;
  onProfile: (req: VesselCardProfileRequest) => void;
  onChat: (args: ChatArgs) => void;
  /** Fires once, whichever way the popup went away. */
  onClosed: () => void;
}

/**
 * Mounts `<app-vessel-card>` inside a MapLibre popup anchored to a coordinate.
 * Owns the component's lifetime so callers only hold the popup.
 */
@Injectable({ providedIn: 'root' })
export class VesselCardPopupService {
  /** Directional offsets clear the 44px vessel icon; omitting an anchor lets MapLibre flip near edges. */
  private static readonly OFFSET = {
    top: [0, 26], 'top-left': [0, 26], 'top-right': [0, 26],
    bottom: [0, -26], 'bottom-left': [0, -26], 'bottom-right': [0, -26],
    left: [26, 0], right: [-26, 0], center: [0, 0],
  } as any;

  constructor(private appRef: ApplicationRef, private injector: EnvironmentInjector) {}

  open(map: maplibregl.Map, lngLat: [number, number] | null, cfg: VesselCardPopupConfig): maplibregl.Popup {
    const ref: ComponentRef<VesselCardComponent> = createComponent(VesselCardComponent, {
      environmentInjector: this.injector,
    });
    ref.setInput('params', cfg.params);
    ref.setInput('prefetched', cfg.prefetched ?? null);
    ref.setInput('identity', cfg.identity);
    ref.setInput('units', cfg.units);
    ref.setInput('chatEnabled', cfg.chatEnabled);
    this.appRef.attachView(ref.hostView);

    const popup = new maplibregl.Popup({
      offset: VesselCardPopupService.OFFSET,
      closeButton: false,
      maxWidth: 'none',
      className: 'obs-trip-popup',
    }).setDOMContent(ref.location.nativeElement);

    const subs = [
      ref.instance.closed.subscribe(() => popup.remove()),
      ref.instance.participantsRequested.subscribe(cfg.onParticipants),
      ref.instance.profileRequested.subscribe(cfg.onProfile),
      ref.instance.chatRequested.subscribe(cfg.onChat),
    ];
    popup.once('close', () => {
      subs.forEach((s) => s.unsubscribe());
      this.appRef.detachView(ref.hostView);
      ref.destroy();
      cfg.onClosed();
    });

    if (lngLat) popup.setLngLat(lngLat);
    popup.addTo(map);
    ref.changeDetectorRef.detectChanges();
    return popup;
  }
}
